/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Preloader } from "@smartsuite/react-ui/lib";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchDocumentDesignById } from "../../api/client";
import { DocumentDesign } from "../../api/types";
import { Canvas } from "../../canvas/Canvas";
import { CanvasManager } from "../../canvas/CanvasManager";
import { getDocumentDesignPaperConfig } from "../../canvas/paper-config";
import { useRenderContext } from "../../canvas/state/useRenderContext";
import { downloadFile } from "./helper";

export const ExportDesignRoute: React.FC = () => {
  const [params] = useSearchParams();

  const documentDesignId = params.get("document_design");
  const recordId = params.get("record");
  const reportName = params.get("report_name");

  const { data: documentDesign } = useQuery({
    enabled: !!documentDesignId,
    queryKey: ["documentDesign", documentDesignId],
    queryFn: () => fetchDocumentDesignById(documentDesignId!),
  });

  if (!documentDesign?.canvas_template || !recordId) {
    return null;
  }

  return (
    <ExportDesign
      design={documentDesign}
      recordId={recordId}
      reportName={reportName}
    />
  );
};

export interface ExportDesignProps {
  design: DocumentDesign;
  recordId: string;
  reportName?: string | null;
}

export const ExportDesign: React.FC<ExportDesignProps> = ({
  design,
  recordId,
  reportName,
}) => {
  const context = useRenderContext(
    design.canvas_template!,
    design.application,
    recordId,
    getDocumentDesignPaperConfig(design),
  );

  const [manager, setManager] = useState<CanvasManager>();
  const [blobUrl, setBlobUrl] = useState<URL | undefined>();

  const exportPdf = useCallback((): void => {
    if (!manager || !design) {
      return;
    }

    manager.toJsPDF(getDocumentDesignPaperConfig(design), (pdf) => {
      const blobUrl = pdf.output("bloburl");
      // if reportName passed download file
      if (reportName) {
        downloadFile(blobUrl.toString(), reportName);
      } else {
        pdf.autoPrint();
        setBlobUrl(blobUrl);
      }
    });
  }, [manager, design, reportName]);

  useEffect(() => {
    if (!context.loading) {
      setTimeout(() => exportPdf(), 2000);
    }
  }, [context.loading, exportPdf]);

  return (
    <>
      {context.loading && <Preloader />}

      {blobUrl && (
        <iframe
          title="Export"
          src={blobUrl.toString()}
          style={{ width: "100%", height: "calc(100vh - 5px)", border: 0 }}
        />
      )}

      {!blobUrl && (
        <div style={{ height: 0, display: "none" }}>
          <Canvas
            state={design.canvas_template!}
            context={context}
            isPreviewing={true}
            onStateChange={() => null}
            onSelectionChange={() => null}
            onReady={setManager}
            dimensions={getDocumentDesignPaperConfig(design)}
          />
        </div>
      )}
    </>
  );
};
